import React, { useEffect, useRef, useState, forwardRef } from 'react';
import { isSafari } from 'utils/fnDevices';
import translate from 'utils/fnTranslate';
import {
    BufferLine,
    ProgressBar,
    ProgressLine,
    Scrubber,
    SeekBarContainer,
    TimeIndicator,
    ProgressAndScrubber,
    SeekBarValue,
    ThumbnailContainer,
    ThumbnailImage,
    HoverLine,
} from './SeekBar.css';

type SeekBarProps = {
    enabled: boolean;
    onSeekInput?: () => void;
    onSeekStart?: () => void;
    onSeekEnd?: () => void;
    onSeekBarCreated: (object: SeekBarElements) => void;
    onMouseHover?: (e: MouseEvent) => void;
    onMouseLeave?: (e: MouseEvent) => void;
};

export type SeekBarElements = {
    scrubber: HTMLInputElement;
    currentTime: HTMLDivElement;
    remainingTime: HTMLDivElement;
    bufferLine: HTMLDivElement;
    hoverLine: HTMLDivElement;
    progressLine: HTMLDivElement;
    scrubberValue: HTMLDivElement;
    thumbnailContainer: HTMLDivElement;
    thumbnailImage: HTMLImageElement;
};

type Language = 'en' | 'et' | 'ru';

const TRANSLATION_FALLBACK: Record<string, Record<Language, string>> = {
    ARIA_SEEKBAR_LABEL: {
        en: 'Playback position',
        et: 'Esituse asukoht',
        ru: 'Позиция воспроизведения',
    },
    ARIA_SEEKBAR_VALUE: {
        en: '%elapsed% of %total%',
        et: '%elapsed%, kokku %total%',
        ru: '%elapsed%, всего %total%',
    },
};

const getLanguage = (): Language => {
    const language = document.documentElement.lang;

    return language === 'en' || language === 'ru' ? language : 'et';
};

const label = (key: keyof typeof TRANSLATION_FALLBACK, vars?: { [key: string]: string }): string => {
    const translation = translate(key, vars);

    if (translation !== key) return translation;

    return Object.keys(vars ?? {}).reduce(
        (text, varKey) => text.replace(`%${varKey}%`, vars[varKey]),
        TRANSLATION_FALLBACK[key][getLanguage()]
    );
};

const formatUnit = (value: number, unit: 'hour' | 'minute', language: Language): string => {
    try {
        return new Intl.NumberFormat(language, { style: 'unit', unit, unitDisplay: 'long' } as Intl.NumberFormatOptions).format(value);
    } catch {
        return `${value} ${unit === 'hour' ? 'h' : 'min'}`;
    }
};

const formatSpokenDuration = (seconds: number): string => {
    const language = getLanguage();
    const totalMinutes = Math.max(0, Math.floor(seconds / 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours === 0) return formatUnit(minutes, 'minute', language);
    if (minutes === 0) return formatUnit(hours, 'hour', language);

    return `${formatUnit(hours, 'hour', language)} ${formatUnit(minutes, 'minute', language)}`;
};

export const updateSeekBarValueText = (scrubber: HTMLInputElement, playedSeconds: number, totalSeconds: number) => {
    if (!scrubber || Number.isNaN(playedSeconds) || Number.isNaN(totalSeconds)) return;

    const text = label('ARIA_SEEKBAR_VALUE', {
        elapsed: formatSpokenDuration(playedSeconds),
        total: formatSpokenDuration(totalSeconds),
    });

    if (scrubber.getAttribute('aria-valuetext') !== text) {
        scrubber.setAttribute('aria-valuetext', text);
    }
};

// eslint-disable-next-line react/display-name
export const SeekBar = forwardRef<HTMLInputElement, SeekBarProps>(
    (
        {
            enabled,
            onSeekInput = () => {},
            onSeekBarCreated = () => {},
            onSeekStart = () => {},
            onSeekEnd = () => {},
            onMouseHover = () => {},
            onMouseLeave = () => {},
        },
        ref
    ) => {
        const scrubberRef = useRef<HTMLInputElement>(null);
        const currentTimeRef = useRef<HTMLDivElement>(null);
        const remainingTimeRef = useRef<HTMLDivElement>(null);
        const bufferLineRef = useRef<HTMLDivElement>(null);
        const hoverLineRef = useRef<HTMLDivElement>(null);
        const progressLineRef = useRef<HTMLDivElement>(null);
        const scrubberValueRef = useRef<HTMLDivElement>(null);
        const thumbnailRef = useRef<HTMLDivElement>(null);
        const thumbnailImageRef = useRef<HTMLImageElement>(null);

        const [seekValue] = useState(0);
        const [seekMin] = useState(0);
        const [seekMax] = useState(1);

        const addEventListeners = () => {
            if (scrubberRef?.current) {
                const scrubber = scrubberRef.current;

                scrubber.addEventListener('mousedown', onSeekStart);
                scrubber.addEventListener('touchstart', onSeekStart);
                scrubber.addEventListener('input', onSeekInput);
                if (!isSafari()) {
                    scrubber.addEventListener('change', onSeekInput);
                }
                scrubber.addEventListener('touchend', onSeekEnd);
                scrubber.addEventListener('mouseup', onSeekEnd);
                scrubber.addEventListener('mousemove', onMouseHover);
                scrubber.addEventListener('mouseleave', onMouseLeave);
            }
        };

        useEffect(() => {
            addEventListeners();
            const scrubber = scrubberRef?.current || null;
            const currentTime = currentTimeRef?.current || null;
            const remainingTime = remainingTimeRef?.current || null;
            const bufferLine = bufferLineRef?.current || null;
            const hoverLine = hoverLineRef?.current || null;
            const progressLine = progressLineRef?.current || null;
            const scrubberValue = scrubberValueRef?.current || null;
            const thumbnailContainer = thumbnailRef?.current || null;
            const thumbnailImage = thumbnailImageRef?.current || null;

            onSeekBarCreated({
                scrubber,
                currentTime,
                remainingTime,
                bufferLine,
                hoverLine,
                progressLine,
                scrubberValue,
                thumbnailContainer,
                thumbnailImage,
            });

            return () => {
                scrubber.removeEventListener('mousedown', onSeekStart);
                scrubber.removeEventListener('touchstart', onSeekStart);
                scrubber.removeEventListener('input', onSeekInput);
                if (!isSafari()) {
                    scrubber.removeEventListener('change', onSeekInput);
                }
                scrubber.removeEventListener('touchend', onSeekEnd);
                scrubber.removeEventListener('mouseup', onSeekEnd);
                scrubber.removeEventListener('mousemove', onMouseHover);
                scrubber.removeEventListener('mouseleave', onMouseLeave);
            };
        }, []);

        return (
            <SeekBarContainer>
                <TimeIndicator data-test-id={'player-seekbar-elapsed-time'} ref={currentTimeRef}>
                    0:00:00
                </TimeIndicator>
                <ProgressAndScrubber>
                    <ProgressBar data-test-id={'player-progress-bar'}>
                        <BufferLine ref={bufferLineRef} />
                        <HoverLine ref={hoverLineRef} />
                        <ProgressLine ref={progressLineRef} />
                    </ProgressBar>
                    <SeekBarValue ref={scrubberValueRef}>0:00:00</SeekBarValue>
                    <ThumbnailContainer ref={thumbnailRef}>
                        <ThumbnailImage ref={thumbnailImageRef} src={undefined} positionX={0} positionY={0} />
                    </ThumbnailContainer>
                    <Scrubber
                        type={'range'}
                        step={'any'}
                        data-test-id={'player-scrubber'}
                        aria-label={label('ARIA_SEEKBAR_LABEL')}
                        min={seekMin ?? 0}
                        max={seekMax ?? 1}
                        defaultValue={seekValue}
                        disabled={!enabled}
                        ref={(inputElement) => {
                            scrubberRef.current = inputElement;
                            if (typeof ref === 'function') {
                                ref(inputElement);
                            } else if (ref) {
                                ref.current = inputElement;
                            }
                        }}
                    />
                </ProgressAndScrubber>
                <TimeIndicator data-test-id={'player-seekbar-remaining-time'} ref={remainingTimeRef}>
                    0:00:00
                </TimeIndicator>
            </SeekBarContainer>
        );
    }
);
